import { z } from 'zod';
import { USER_ROLES } from './auth.js';
import { MONEY_POLICY, VAT_RATE_POLICY, decimalString } from './decimal.js';
import { systemMailDeliverySchema } from './system-mail.js';
/** ADR-01 principal türleri: kullanıcı, departman, şirket geneli (sentinel). */
export const principalSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('user'), id: z.guid() }),
    z.object({ type: z.literal('dept'), id: z.guid() }),
    z.object({ type: z.literal('company') }),
]);
export const RECORD_TYPES = [
    'contact',
    'company',
    'lead',
    'opportunity',
    'proposal',
    'order',
    'purchase_order',
    'contract',
    'activity',
    'ticket',
    'product',
    // Plan yer tutucusu (ilk katalog); gerçek form belgesi tipi M5 WP 5.1'de `form_document` (migration 0063)
    'custom_document',
    'form_document',
    // v3.1 S10 (migration 20261010_0140): ekip gelen kutusu konuşması
    'conversation',
];
/** Uygulama bölümleri (menü); departman bazlı görünürlük. */
export const SECTIONS = [
    'newsfeed',
    'leads',
    'opportunities',
    'proposals',
    'orders',
    'purchase_orders',
    'contracts',
    'dashboard',
    'goals',
    'activities',
    'calendar',
    'tickets',
    'forms',
    'contacts',
    'companies',
    'products',
    'files',
    'users',
    'automation',
];
/** CRM Lite rolünün görebileceği bölümler (ÜK: Teamgram CRM Lite tanımı keşifte gözlenmedi). */
export const CRM_LITE_SECTIONS = [
    'newsfeed',
    'contacts',
    'companies',
    'activities',
    'calendar',
];
const pctValue = z.union([z.number().min(0).max(100), decimalString(VAT_RATE_POLICY)]);
const moneyValue = z.union([z.number().min(0), decimalString(MONEY_POLICY)]);
export const approvalPolicySchema = z
    .object({
    mode: z.enum(['none', 'always', 'threshold']).default('none'),
    // M3: ondalık metin kabul edilir (para için JS number kullanılmaz); eski kayıtlardaki sayılar da okunur
    maxDiscountPct: pctValue.nullable().default(null),
    minMarginPct: pctValue.nullable().default(null),
    minAmount: moneyValue.nullable().default(null),
    amountThreshold: moneyValue.nullable().default(null),
    discountThresholdPct: pctValue.nullable().default(null),
    stockCheck: z.boolean().default(false),
    approverDeptId: z.guid().nullable().default(null),
})
    .strict();
export const deptShareSchema = z
    .object({
    recordType: z.enum(RECORD_TYPES),
    defaultReaders: z.array(principalSchema).max(50).default([]),
    defaultWriters: z.array(principalSchema).max(50).default([]),
    approval: approvalPolicySchema.prefault({}),
})
    .strict();
export const departmentSchema = z.object({
    id: z.guid(),
    name: z.string(),
    parentId: z.guid().nullable(),
    sortOrder: z.number().int(),
    depth: z.number().int(),
    userCount: z.number().int(),
});
export const createDepartmentInputSchema = z
    .object({
    name: z.string().trim().min(1).max(120),
    parentId: z.guid().nullable().optional(),
    sortOrder: z.number().int().min(0).max(100000).optional(),
})
    .strict();
export const updateDepartmentInputSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    parentId: z.guid().nullable().optional(),
    sortOrder: z.number().int().min(0).max(100000).optional(),
})
    .strict();
export const sectionVisibilityInputSchema = z
    .object({ sections: z.partialRecord(z.enum(SECTIONS), z.boolean()) })
    .strict();
export const CAPABILITY_NAMES = [
    'settings.manage',
    'users.invite',
    'users.manage',
    'departments.manage',
    'records.read',
    'records.read.own',
    'records.write',
    'records.write.lite',
    'records.delete',
    'records.export',
    'records.import',
    'records.bulk',
    'sms.send',
    'ai.use',
    // WP 2.4 — kayıtlı listeler (dar yetkiler; records.write bunları İÇERMEZ)
    'lists.create', // özel (private) liste oluşturma/kendi listesini yönetme
    'lists.share', // listeyi tenant geneline açma (public)
    'lists.manage', // başkasının listesini yönetme (yönetici)
    // WP 2.5 — etiket tanımı yönetimi (yeniden adlandırma/renk/silme); kayda etiket eklemek kayıt yazma yetkisiyle yapılır
    'tags.manage',
    // WP 2.5 — başka kullanıcıya hatırlatıcı atama (alıcının kaydı görebilmesi ayrıca gerekir)
    'reminders.assign',
    // WP 2.10 — ürün maliyeti (alan, kolon, maliyet tarihçesi), toplu fiyat + müşteriye görünmez fiyatlar, manuel stok hareketi
    'products.cost_view',
    'products.price_manage',
    'stock.adjust',
    // v3.1 S9 (D2) — aday olay havuzu: başarısız olayı yeniden işleme ve olay listesi; ham yükü görme ayrı ve denetimli yetenek
    'lead_events.manage',
    'lead_events.view_raw',
    // v3.1 S11 — reklam harcaması/performansı OKUMA (rapor) ve reklam değişikliği İSTEĞİ (Castinflow reklam hesabına yazmaz)
    'ads.read',
    'ads.request',
    // v3.1 S11 — reklam değişikliği isteğini ONAYLAMA/REDDETME (onaylanan istek yürütme sistemine iletilir)
    'ads.approve',
];
export const capabilityOverrideSchema = z
    .object({
    grant: z.array(z.enum(CAPABILITY_NAMES)).max(20).default([]),
    revoke: z.array(z.enum(CAPABILITY_NAMES)).max(20).default([]),
})
    .strict();
export const userListItemSchema = z.object({
    id: z.guid(),
    email: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    title: z.string(),
    role: z.enum(USER_ROLES),
    isOwner: z.boolean(),
    status: z.enum(['invited', 'active', 'suspended', 'left']),
    deptId: z.guid().nullable(),
    totpEnabled: z.boolean(),
    lastLoginAt: z.string().nullable(),
    capabilities: capabilityOverrideSchema,
    /**
     * M7 T2: davet iletisinin posta durumu — yalnız `invited` kullanıcıda dolu; iş kaydı bulunamazsa `null`. Alan yoksa sunucu
     * bu bilgiyi yayımlamıyordur (önceki sürüm). "Sağlayıcı kabul etti" alıcıya teslim anlamına gelmez.
     */
    inviteMail: systemMailDeliverySchema.nullable().optional(),
    /**
     * v3.1 A3: daveti kabul edilmeden iptal edilmiş kullanıcı (durum `left`, parolası yok). Yeniden etkinleştirilemez; aynı
     * adres için davet yeniden gönderilir. Alan yoksa sunucu bu bilgiyi yayımlamıyordur (önceki sürüm).
     */
    inviteCancelled: z.boolean().optional(),
});
export const updateUserInputSchema = z
    .object({
    role: z.enum(USER_ROLES).optional(),
    deptId: z.guid().nullable().optional(),
    status: z.enum(['active', 'suspended', 'left']).optional(),
    title: z.string().trim().max(120).optional(),
    firstName: z.string().trim().min(1).max(80).optional(),
    lastName: z.string().trim().min(1).max(80).optional(),
    capabilities: capabilityOverrideSchema.optional(),
    /**
     * G3 (sahip kararı E): `capabilities` (yetki istisnası) değişikliğinin ZORUNLU gerekçesi. H2: hassas yetki ekleyen değişiklik
     * bu yoldan uygulanmaz (409 `sensitive_approval_required` → `POST /users/:id/capability-requests`); `confirmSensitive`
     * yalnız önceki istemcilerle uyum için kabul edilir, onay yerine GEÇMEZ.
     */
    capabilityReason: z.string().trim().min(10, 'gerekçe en az 10 karakter').max(500).optional(),
    confirmSensitive: z.boolean().optional(),
})
    .strict();
// ---------------------------------------------------------------------------------------------------------------------
// G3 (sahip kararı E, 24 Ekim 2026): kullanıcı yetki istisnaları — rolün ÜSTÜNE kullanıcıya özel ekleme (grant) / kaldırma
// (revoke). Öncelik: sahip her zaman tam yetkili; sonra rolün yetenekleri; istisna ile eklenen eklenir, istisna ile kaldırılan
// kaldırılır (kaldırma eklemeden sonra uygulanır). Sunucu kuralı `apps/api/src/lib/capabilities.ts` `capabilitiesFor`.
// ---------------------------------------------------------------------------------------------------------------------
/** Yeteneklerin Türkçe adları (yönetim ekranı ve denetim kaydı) */
export const CAPABILITY_LABELS = {
    'settings.manage': 'Çalışma alanı ayarlarını yönetme',
    'users.invite': 'Kullanıcı davet etme',
    'users.manage': 'Kullanıcıları yönetme',
    'departments.manage': 'Departman paylaşım ve onay ayarları',
    'records.read': 'Kayıtları görüntüleme (kapsamındaki)',
    'records.read.own': 'Yalnız kendi kayıtlarını görüntüleme',
    'records.write': 'Kayıt oluşturma ve düzenleme',
    'records.write.lite': 'Kayıtları sınırlı alanlarda düzenleme',
    'records.delete': 'Kayıt silme',
    'records.export': 'Dışa aktarma',
    'records.import': 'İçe aktarma',
    'records.bulk': 'Toplu işlem',
    'sms.send': 'SMS gönderme',
    'ai.use': 'Yapay zekâ özelliklerini kullanma',
    'lists.create': 'Kişisel liste oluşturma',
    'lists.share': 'Listeyi çalışma alanıyla paylaşma',
    'lists.manage': 'Başkasının listesini yönetme',
    'tags.manage': 'Etiket tanımlarını yönetme',
    'reminders.assign': 'Başkasına hatırlatıcı atama',
    'products.cost_view': 'Ürün maliyetini görme',
    'products.price_manage': 'Toplu fiyat yönetimi',
    'stock.adjust': 'Elle stok hareketi',
    'lead_events.manage': 'Aday olaylarını yönetme',
    'lead_events.view_raw': 'Aday olayının ham verisini görme',
    'ads.read': 'Reklam verilerini görme',
    'ads.request': 'Reklam değişikliği isteme',
    'ads.approve': 'Reklam değişikliği onaylama',
};
/**
 * K1 (25 Ekim 2026) — yetkinin sunucuda KORUDUĞU işlem. Etkisiz yetki kalmasın diye her yetki en az bir sunucu yolunda
 * istenir; istenmeyenler yalnız bu listededir ve ekran bunu açıkça yazar (kilit: `apps/api/test/unit/etkisiz-yetki.test.ts`).
 *
 * - `ai.use`: asistanı (yapay zekâ çalıştırıcısını) ÇALIŞTIRAN işlemleri başlatmak — konuşmayı asistana devretmek / durdurulan
 *   asistanı sürdürmek, kişi/şirket araştırması talep etmek ve onaylamak, asistan profili oluşturmak / sürdürmek, sınama
 *   (demo) iletisi. Başlatılan iş her yürütme adımında (kira, yanıtın gönderimi) başlatanın bu yetkisini yeniden denetler.
 *   Asistan önerisini onaylamak/reddetmek insan kararıdır, asistanı çalıştırmaz (kayıt yazma yetkisiyle kalır).
 * - `sms.send`: bu üründe SMS gönderen yol YOKTUR (kanal türleri e-posta, iş mesajlaşma hattı, simülatör; otomasyon ve aday
 *   sekansında SMS adımı yok; SMS bağlantısı "Desteklenmeyen bağlantılar" altında). Yetki bugün hiçbir işlemi açmaz. SMS
 *   gönderimi eklenirse o yol bu yetkiyi ister.
 */
export const CAPABILITIES_WITHOUT_OPERATION = {
    'sms.send': 'Bu üründe SMS gönderimi yoktur; bu yetki bugün hiçbir işlemi açmaz.',
};
/**
 * L1 — yetki istisnası ekranında yetkinin ne AÇTIĞI ve ne AÇMADIĞI (kısa, düz Türkçe). `ai.use` varsayılan rollere eklenmez; yönetici
 * seçili kullanıcıya istisna ile verir. Kanıt: `apps/api/test/integration/research.test.ts` § 9b, `agent-usage.test.ts` § 11.
 */
export const CAPABILITY_NOTES = {
    'ai.use': 'Açar: konuşmayı asistana devretmek, asistanı sürdürmek ve yeni araştırma talebi açmak. Açmaz: asistan, bağlantı, sır ve bütçe ayarları (ayrıca ayar yönetimi yetkisi gerekir); bütçe sınırını, araştırma onayını ve kayıt erişimini aşmaz. Önceki araştırma sonuçlarını ve konuşma geçmişini okumak için gerekmez.',
};
/** `ai.use` olmadan asistanı çalıştıran bir işlem istendiğinde (sunucu 403 `ai_use_required`, ekran aynı metni yazar) */
export const AI_USE_REQUIRED_TEXT = 'Bu işlem asistanı çalıştırır ve “Yapay zekâ özelliklerini kullanma” yetkisi ister. Yetki için kullanıcı yöneticinize başvurun.';
/**
 * Hassas yetkiler listesi ve iki adımlı onay sözleşmesi `capability-approval.ts`'tedir (H2; tek kaynak).
 */
export const CAPABILITY_EXCEPTION_REASON = { min: 10, max: 500 };
/** `PUT /users/:id/capability-exceptions` gövdesi */
export const capabilityExceptionsInputSchema = z
    .object({
    grant: z.array(z.enum(CAPABILITY_NAMES)).max(20).default([]),
    revoke: z.array(z.enum(CAPABILITY_NAMES)).max(20).default([]),
    /** Değişiklik gerekçesi (zorunlu; denetim kaydına yazılır) */
    reason: z
        .string()
        .trim()
        .min(CAPABILITY_EXCEPTION_REASON.min, 'gerekçe en az 10 karakter')
        .max(CAPABILITY_EXCEPTION_REASON.max, 'gerekçe en çok 500 karakter'),
    /** Önceki istemcilerle uyum için kabul edilir; H2'den beri hassas yetki için onay yerine geçmez (iki adımlı talep) */
    confirmSensitive: z.boolean().default(false),
    /** İyimser kilit: ekranın gördüğü (normalize) istisna; değiştiyse 409 */
    expected: capabilityOverrideSchema,
})
    .strict();
export const CAPABILITY_ORIGINS = ['role', 'granted', 'revoked', 'none'];
export const CAPABILITY_ORIGIN_LABELS = {
    role: 'Rolden gelen',
    granted: 'İstisna ile eklenen',
    revoked: 'İstisna ile kaldırılan',
    none: 'Yok',
};
export const capabilityExceptionsViewSchema = z.object({
    userId: z.guid(),
    role: z.enum(USER_ROLES),
    isOwner: z.boolean(),
    /** Kullanıcı etkin değilse (davetli/askıda/ayrılmış) hiçbir yetenek etkin değildir */
    active: z.boolean(),
    /** Saklanan (normalize) istisna */
    override: capabilityOverrideSchema,
    rows: z.array(z.object({
        capability: z.enum(CAPABILITY_NAMES),
        label: z.string(),
        sensitive: z.boolean(),
        fromRole: z.boolean(),
        origin: z.enum(CAPABILITY_ORIGINS),
        /** Kullanıcıda etkin mi (sahip: her zaman) */
        effective: z.boolean(),
    })),
    /** Çağıran değiştirebilir mi; değiştiremiyorsa neden */
    editable: z.boolean(),
    blockedReason: z.enum(['owner', 'self', 'outranks', 'no_permission']).nullable(),
    /** Son değişiklikler (denetim kaydından; yeniden eskiye) */
    history: z.array(z.object({
        at: z.string(),
        actor: z.object({ id: z.string(), name: z.string() }).nullable(),
        reason: z.string().nullable(),
        added: z.array(z.enum(CAPABILITY_NAMES)),
        removed: z.array(z.enum(CAPABILITY_NAMES)),
        sensitiveGranted: z.array(z.enum(CAPABILITY_NAMES)),
        /** L1: hassas yetki hangi onayla verildi (iki kişili / sahibin doğrudan onayı); onaysız değişiklikte yok */
        approvalKind: z.enum(['two_person', 'owner_direct']).optional(),
    })),
});
/**
 * Saf: istisnayı rol karşısında normalize eder — rolde zaten olan "ekleme" ve rolde olmayan "kaldırma" etkisizdir, saklanmaz;
 * aynı yetenek hem eklenip hem kaldırılamaz (`conflict`).
 */
export function normalizeCapabilityOverride(roleCaps, o) {
    const role = new Set(roleCaps);
    const grant = [...new Set(o.grant)].filter((c) => !role.has(c)).sort();
    const revoke = [...new Set(o.revoke)].filter((c) => role.has(c)).sort();
    const conflict = [...new Set(o.grant)].filter((c) => o.revoke.includes(c)).sort();
    return { override: { grant, revoke }, conflict };
}
/** Etkin izin görünümü: rol + override + departman kapsamı (ACL cache içeriği). */
export const effectivePermissionsSchema = z.object({
    userId: z.guid(),
    role: z.enum(USER_ROLES),
    isOwner: z.boolean(),
    capabilities: z.array(z.enum(CAPABILITY_NAMES)),
    /** Veri kapsamı: all | department (kendi departmanı + alt departmanlar) | own */
    scope: z.enum(['all', 'department', 'own']),
    deptId: z.guid().nullable(),
    /** Kapsamdaki departman kimlikleri (closure; kendi departmanı + alt departmanlar) */
    deptScopeIds: z.array(z.guid()),
    /** Kullanıcının departmanı + üst departmanları (departman paylaşımları buradan iner; WP 2.2) */
    deptAncestorIds: z.array(z.guid()).default([]),
    sections: z.array(z.enum(SECTIONS)),
    aclVersion: z.number().int(),
    /** H2: en yakın süreli istisna bitişi (yoksa null; alan yoksa sunucu bu bilgiyi yayımlamıyordur) — önbellek bu anda düşer */
    capabilityExpiresAt: z.string().nullable().optional(),
});
//# sourceMappingURL=org.js.map